/**
 * SkillDelivery Platform
 * Workspace Component - Dynamic Workspace System
 *
 * Phase 0.8 P3: Cleaned up workspace
 * - Removed redundant "Your Dashboard" section (Dashboard is separate page)
 * - Removed Quick SkillBot widget (floating chat exists on all pages)
 * - Workspace now shows: Goals Section + Community quick view only
 * - Entity-specific widgets will be added in Phase 1 redesign
 *
 * Conference Sprint S1f: Updated x-Connect tab to Community quick summary with link to full page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../../services/api';
import WorkspaceHeader from './WorkspaceHeader';
import GoalsSection from '../workspace/GoalsSection';
import FocusAreaSelector from '../workspace/FocusAreaSelector';
import { Loader, Target, Settings, Users, Sparkles, UserPlus, ArrowRight, Link2 } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import './Workspace.css';

const Workspace = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFocusSelector, setShowFocusSelector] = useState(false);
  const [activeTab, setActiveTab] = useState('goals'); // 'goals' | 'community'

  // Community quick stats - Conference Sprint S1f
  const [communityStats, setCommunityStats] = useState({
    suggestions: 0,
    pendingRequests: 0,
    connections: 0
  });
  const [communityLoading, setCommunityLoading] = useState(false);

  // Check for ?tab=xconnect or ?tab=community query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'xconnect' || tabParam === 'community') {
      setActiveTab('community');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchWorkspaceData();
  }, []);

  // Fetch community quick stats - Conference Sprint S1f
  const fetchCommunityStats = useCallback(async () => {
    try {
      setCommunityLoading(true);
      const [suggestionsRes, incomingRes, connectionsRes] = await Promise.all([
        api.get('/xconnect/suggestions'),
        api.get('/xconnect/requests/incoming'),
        api.get('/xconnect/connections')
      ]);

      setCommunityStats({
        suggestions: suggestionsRes.data?.suggestions?.length || 0,
        pendingRequests: incomingRes.data?.requests?.length || 0,
        connections: connectionsRes.data?.connections?.length || 0
      });
    } catch (err) {
      console.error('[Workspace] Error fetching community stats:', err);
    } finally {
      setCommunityLoading(false);
    }
  }, []);

  // Fetch community stats when tab is active
  useEffect(() => {
    if (activeTab === 'community') {
      fetchCommunityStats();
    }
  }, [activeTab, fetchCommunityStats]);

  const fetchWorkspaceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch profile using API service
      const profileData = await api.request('/workspace/profile');
      if (!profileData.success) throw new Error('Failed to fetch profile');
      setProfile(profileData.profile);

      setLoading(false);
    } catch (err) {
      console.error('Workspace data fetch error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleLinkedInUpdate = (newUrl) => {
    setProfile(prev => ({ ...prev, linkedinUrl: newUrl }));
  };

  const handleFocusAreasUpdated = () => {
    setShowFocusSelector(false);
    fetchWorkspaceData();
  };

  if (loading) {
    return <LoadingSpinner message="Loading your workspace..." />;
  }

  if (error) {
    return (
      <div className="workspace-error">
        <p>Error loading workspace: {error}</p>
        <button onClick={fetchWorkspaceData} className="workspace-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  const accountType = profile?.accountType || 'individual';

  return (
    <div className="workspace-container">
      {/* Workspace Header */}
      <WorkspaceHeader
        profile={profile}
        onLinkedInUpdate={handleLinkedInUpdate}
      />

      {/* Tab Bar */}
      <div className="workspace-actions-bar">
        <button
          onClick={() => setActiveTab('goals')}
          className={`workspace-action-btn ${activeTab === 'goals' ? 'active' : ''}`}
        >
          <Target size={16} />
          <span>Goals & Priorities</span>
        </button>
        <button
          onClick={() => setActiveTab('community')}
          className={`workspace-action-btn ${activeTab === 'community' ? 'active' : ''}`}
        >
          <Users size={16} />
          <span>Community</span>
        </button>
        <button
          onClick={() => setShowFocusSelector(true)}
          className="workspace-action-btn secondary"
        >
          <Settings size={16} />
          <span>Customize Focus</span>
        </button>
      </div>

      {/* Focus Area Selector Modal */}
      {showFocusSelector && (
        <FocusAreaSelector
          entityType={accountType}
          onClose={() => setShowFocusSelector(false)}
          onSave={handleFocusAreasUpdated}
        />
      )}

      {/* Goals Section - Visible when Goals tab active */}
      {activeTab === 'goals' && (
        <div className="workspace-section">
          <GoalsSection userId={user?.id} />
        </div>
      )}

      {/* Community Quick View - Visible when Community tab active (Conference Sprint S1f) */}
      {activeTab === 'community' && (
        <div className="workspace-section workspace-community-summary">
          <div className="community-summary-header">
            <Users size={24} className="header-icon" />
            <div>
              <h3>Community Quick View</h3>
              <p>Your professional network at a glance</p>
            </div>
          </div>

          {communityLoading ? (
            <div className="community-loading">
              <Loader className="workspace-spinner" size={24} />
              <span>Loading stats...</span>
            </div>
          ) : (
            <>
              <div className="community-quick-stats">
                <div className="quick-stat">
                  <div className="stat-icon suggestions">
                    <Sparkles size={20} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{communityStats.suggestions}</span>
                    <span className="stat-label">New Matches</span>
                  </div>
                </div>
                <div className="quick-stat">
                  <div className="stat-icon requests">
                    <UserPlus size={20} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{communityStats.pendingRequests}</span>
                    <span className="stat-label">Pending Requests</span>
                  </div>
                </div>
                <div className="quick-stat">
                  <div className="stat-icon connections">
                    <Link2 size={20} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{communityStats.connections}</span>
                    <span className="stat-label">Connections</span>
                  </div>
                </div>
              </div>

              <Link to="/community" className="view-community-btn">
                <span>Open Community Hub</span>
                <ArrowRight size={18} />
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Workspace;
